/** The device buffer (decision 0051): encrypted chunks waiting to upload, in IndexedDB. Ciphertext only; the key is not
 * here. A chunk leaves when its PUT succeeds (200 or 412) or when the accountant deliberately discards the recording.
 * Nothing here clears unsent audio on its own: not on Stop, not on sign-out, not on load. */
import type { Track } from "./types.js";

export interface ChunkMeta {
  recording_id: string;
  track: Track;
  seq: number;
  started_ms: number;
  duration_ms: number;
  content_type: string;
  bytes: number; // plaintext length, as the server will be told
}

export interface RecordingMeta {
  recording_id: string;
  host?: string; // whose audio this is (a short hash); absent on older buffers, which are then only ever cleared by their own expiry
  meeting_id: string; // the client is deliberately not stored here: the device keeps only what it needs to upload
  started_at: string;
  expires_at: string; // unsent_expires_at
  tracks: Track[];
  chunk_seconds: number;
  mime_type: string;
}

export interface ChunkStore {
  putMeta(meta: RecordingMeta): Promise<void>;
  listMetas(): Promise<RecordingMeta[]>;
  putChunk(meta: ChunkMeta, ciphertext: Uint8Array<ArrayBuffer>): Promise<void>;
  getChunk(recordingId: string, track: Track, seq: number): Promise<Uint8Array<ArrayBuffer> | null>;
  deleteChunk(recordingId: string, track: Track, seq: number): Promise<void>;
  listChunks(recordingId: string): Promise<ChunkMeta[]>;
  deleteRecording(recordingId: string): Promise<void>;
}

const k = (id: string, track: Track, seq: number) => `${id}/${track}/${String(seq).padStart(6, "0")}`;

/** For tests and as the fallback when IndexedDB is unavailable. */
export class MemoryChunkStore implements ChunkStore {
  metas = new Map<string, RecordingMeta>();
  chunks = new Map<string, { meta: ChunkMeta; data: Uint8Array<ArrayBuffer> }>();
  failWrites = false;
  async putMeta(meta: RecordingMeta) {
    this.metas.set(meta.recording_id, meta);
  }
  async listMetas() {
    return [...this.metas.values()];
  }
  async putChunk(meta: ChunkMeta, ciphertext: Uint8Array<ArrayBuffer>) {
    if (this.failWrites) throw new DOMException("full", "QuotaExceededError");
    this.chunks.set(k(meta.recording_id, meta.track, meta.seq), { meta, data: ciphertext });
  }
  async getChunk(id: string, track: Track, seq: number) {
    return this.chunks.get(k(id, track, seq))?.data ?? null;
  }
  async deleteChunk(id: string, track: Track, seq: number) {
    this.chunks.delete(k(id, track, seq));
  }
  async listChunks(id: string) {
    return [...this.chunks.values()].filter((c) => c.meta.recording_id === id).map((c) => c.meta);
  }
  async deleteRecording(id: string) {
    for (const key of [...this.chunks.keys()]) if (key.startsWith(id + "/")) this.chunks.delete(key);
    this.metas.delete(id);
  }
}

const DB = "pk-recorder";

export class IndexedDbChunkStore implements ChunkStore {
  private dbPromise: Promise<IDBDatabase> | null = null;
  private db(): Promise<IDBDatabase> {
    this.dbPromise ??= new Promise((resolve, reject) => {
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore("metas", { keyPath: "recording_id" });
        req.result.createObjectStore("chunks");
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error ?? new Error("IndexedDB failed to open."));
    });
    return this.dbPromise;
  }
  private async tx<T>(stores: string | string[], mode: IDBTransactionMode, run: (tx: IDBTransaction) => IDBRequest | void): Promise<T> {
    const db = await this.db();
    return new Promise<T>((resolve, reject) => {
      const tx = db.transaction(stores, mode);
      const req = run(tx);
      tx.oncomplete = () => resolve((req ? (req as IDBRequest).result : undefined) as T);
      tx.onerror = () => reject(tx.error ?? new Error("IndexedDB write failed."));
      tx.onabort = () => reject(tx.error ?? new DOMException("aborted", "QuotaExceededError"));
    });
  }
  putMeta(meta: RecordingMeta) {
    return this.tx<void>("metas", "readwrite", (tx) => {
      tx.objectStore("metas").put(meta);
    });
  }
  listMetas() {
    return this.tx<RecordingMeta[]>("metas", "readonly", (tx) => tx.objectStore("metas").getAll());
  }
  putChunk(meta: ChunkMeta, ciphertext: Uint8Array<ArrayBuffer>) {
    return this.tx<void>("chunks", "readwrite", (tx) => {
      tx.objectStore("chunks").put({ meta, data: ciphertext }, k(meta.recording_id, meta.track, meta.seq));
    });
  }
  async getChunk(id: string, track: Track, seq: number) {
    const row = await this.tx<{ data: Uint8Array<ArrayBuffer> } | undefined>("chunks", "readonly", (tx) => tx.objectStore("chunks").get(k(id, track, seq)));
    return row?.data ?? null;
  }
  deleteChunk(id: string, track: Track, seq: number) {
    return this.tx<void>("chunks", "readwrite", (tx) => {
      tx.objectStore("chunks").delete(k(id, track, seq));
    });
  }
  async listChunks(id: string) {
    const rows = await this.tx<Array<{ meta: ChunkMeta }>>("chunks", "readonly", (tx) =>
      tx.objectStore("chunks").getAll(IDBKeyRange.bound(`${id}/`, `${id}/￿`)),
    );
    return rows.map((r) => r.meta);
  }
  async deleteRecording(id: string) {
    await this.tx<void>(["chunks", "metas"], "readwrite", (tx) => {
      tx.objectStore("chunks").delete(IDBKeyRange.bound(`${id}/`, `${id}/￿`));
      tx.objectStore("metas").delete(id);
    });
  }
}

/** IndexedDB if there is one, otherwise memory (and the engine warns that nothing survives a reload). */
export function defaultStore(): { store: ChunkStore; persistent: boolean } {
  if (typeof indexedDB !== "undefined") return { store: new IndexedDbChunkStore(), persistent: true };
  return { store: new MemoryChunkStore(), persistent: false };
}

/** Wipes the whole buffer database: for tests and the explicit "forget everything on this device" path only. */
export function deleteRecorderDatabase(): void {
  try {
    indexedDB.deleteDatabase(DB);
  } catch {
    /* none */
  }
}
